import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarRef, MatSnackBarVerticalPosition, TextOnlySnackBar } from '@angular/material/snack-bar';
import { ErrorShowDialogComponent } from '@app/modules/shared/components/error-show-dialog/error-show-dialog.component';
import { BehaviorSubject, elementAt, Observable, windowTime } from 'rxjs';
import { Base64 } from 'js-base64';
import { DateTime } from 'luxon';
import { DialogConfig, UrlConfig } from '@servicemind.tis/tis-image-and-file-upload-and-view';
import { ColumnCustomizationUrlConfig } from '@servicemind.tis/angular-smart-data-table';
import { TisSnackbarComponent, TisSnackbarData } from '@app/modules/shared/components/tis-snackbar/tis-snackbar.component';

const mapping: any = {
  0: "Ocean",
  1: "Star",
  2: "Tree",
  3: "Cloud",
  4: "River",
  5: "Mountain",
  6: "Sun",
  7: "Sky",
  8: "Sand",
  9: "Moon",
};

export interface SnackbarConfig {
  /** The main message text to display. */
  message: string;
  /** Preset styling type — applies a matching CSS panel class automatically. */
  type?: 'success' | 'error' | 'warning' | 'info';
  /** Label for the snackbar action button. Defaults to '' (no button). */
  actionLabel?: string;
  /** Auto-dismiss duration in ms. Defaults to 3000. */
  duration?: number;
  /** Horizontal position of the snackbar. Defaults to 'center'. */
  horizontalPosition?: MatSnackBarHorizontalPosition;
  /** Vertical position of the snackbar. Defaults to 'bottom'. */
  verticalPosition?: MatSnackBarVerticalPosition;
  /** Additional CSS panel class(es) to merge with the type preset. */
  panelClass?: string | string[];
}

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  private traceQrData: any;
  private generatedStrings: Set<string>;

  private currentCompanyId$: BehaviorSubject<number | string> = new BehaviorSubject<number | string>(0);
  private companyCtrlStatus$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  private companyList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private loadingCompanyList$ = new BehaviorSubject<boolean>(true);
  public currentCompanyId = this.currentCompanyId$.asObservable();
  public currentCompanyStatus = this.companyCtrlStatus$.asObservable();
  public companyList = this.companyList$.asObservable();
  public loadingCompanyList = this.loadingCompanyList$.asObservable();

  public urlConfigForImage: UrlConfig = {
    getUploadUrl: '/image/get-upload-url',
    attachToEntity: '/image/attach-to-entity',
    updateTag: '/image/update-tag',
    updateSequence: '/image/update-sequence',
    removeImage: '/image/delete',
  };

  public urlConfigForFile: UrlConfig = {
    getUploadUrl: '/file/get-upload-url',
    attachToEntity: '/file/attach-to-entity',
    updateTag: '/file/update-tag',
    updateSequence: '/file/update-sequence',
    removeImage: '/file/delete',
  };

  public dialogConfigForImage: DialogConfig = {
    panelClass: 'tis-simple-confirmation',
    title: 'Delete Image',
    message: 'Are you sure, you want to delete this image?',
    iconClass: "tis-text-danger",
    icon: "delete",
    approveButtonText: 'Yes, delete',
    approveButtonClass: "tis-text-upper tis-btn-danger",
    cancelButtonText: 'No',
    cancelButtonClass: "tis-text-upper tis-btn-primary"
  }

  public dialogConfigForFile: DialogConfig = {
    panelClass: 'tis-simple-confirmation',
    title: 'Delete File',
    message: 'Are you sure, you want to delete this file?',
    iconClass: "tis-text-danger",
    icon: "delete",
    approveButtonText: 'Yes, delete',
    approveButtonClass: "tis-text-upper tis-btn-danger",
    cancelButtonText: 'No',
    cancelButtonClass: "tis-text-upper tis-btn-primary"
  }
  
  public columnCustomizationUrlConfig: ColumnCustomizationUrlConfig = {
    list: '/user-customisation/get-columns-templates',
    add: '/user-customisation/add-columns-template',
    update: '/user-customisation/update-columns-template',
    delete: '/user-customisation/delete-columns-template',
    getSelectedTemplate: '/user-customisation/get-selected-columns-template',
    updateSelectedTemplate: '/user-customisation/update-selected-columns-template'
  };

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private http: HttpClient
  ) {
    this.generatedStrings = new Set<string>();
  }

  verifyLoginToken(token: string): Observable<any> {
    const body = JSON.stringify({ token });
    return this.http.post(`/ease-of-access/verify-login-token`, body);
  }

  setCurrentCompanyId(companyId: number | string) {
    return this.currentCompanyId$.next(companyId);
  }

  setCurrentCompanyCtrlStatus(status: number) {
    return this.companyCtrlStatus$.next(status);
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
  };

  showHttpErrorMsg(error: HttpErrorResponse, duration = 5000) {
    console.error('httpError: ', error);

    let errorMessage = 'Some Unknown Error Occurred.';
    let errorCode = 'Unknown Error';
    const httpError = error;

    if (httpError.status >= 400) {
      const errorFromServer = httpError.error?.errors || httpError.error || [];
      if (Array.isArray(errorFromServer) && errorFromServer.length > 0) {
        errorMessage = errorFromServer[0].message;
        errorCode = errorFromServer[0].code;
      }
    } else if (httpError.status < 100) {
      errorMessage = httpError.message;
      errorCode = httpError.statusText;
    }

    if (errorCode == "VALIDATION_ERROR" || errorCode == "NOT_FOUND_ERROR" || errorCode == "THIRD_PARTY_API_ERROR") {
      let confirmBoxData: any = {
        title: "Error !",
        message: errorMessage,
        icon: "error",
        iconClass: "tis-text-danger",
        buttonText: "Ok",
        buttonClass: "tis-btn-primary",
      };

      const dialogRef: MatDialogRef<ErrorShowDialogComponent> = this.dialog.open(ErrorShowDialogComponent, {
        width: "550px",
        panelClass: ['tis-simple-confirmation'],
        data: confirmBoxData,
        disableClose: false,
      });

      return dialogRef;

    } else {
      const snackbarRef: MatSnackBarRef<TextOnlySnackBar> = this.snackBar.open(errorMessage, 'Error', { duration: duration });
      return snackbarRef;
    }

  }

  showSuccessMsg(message: string, title: string, duration = 5000) {
    this.snackBar.open(message, title, {
      duration: duration
    })
  }

  showErrorMsg(message: string, title: string, duration = 5000) {
    this.snackBar.open(message, title, {
      duration: duration
    })
  }

  showCustomPopup(
    message: string,
    duration: number = 5000,
    panelClass?: string[] | string,
    horizontalPosition: MatSnackBarHorizontalPosition = 'center',
    verticalPosition: MatSnackBarVerticalPosition = 'bottom'
  ) {
    this.snackBar.open(message, '', {
      duration,
      panelClass,
      horizontalPosition,
      verticalPosition,
    });
  }

  /**
   * Fully dynamic snackbar — use anywhere instead of showSuccessMsg / showErrorMsg.
   *
   * @example
   * // Simple success
   * this.helper.showSnackbar({ message: r?.message || 'Saved!', type: 'success' });
   *
   * // Error with custom duration
   * this.helper.showSnackbar({ message: 'Something went wrong', type: 'error', duration: 4000 });
   *
   * // Warning at the top-right
   * this.helper.showSnackbar({
   *   message: 'Check your input',
   *   type: 'warning',
   *   horizontalPosition: 'right',
   *   verticalPosition: 'top',
   * });
   *
   * // Fully custom — no preset type
   * this.helper.showSnackbar({
   *   message: 'Custom note',
   *   actionLabel: 'Dismiss',
   *   duration: 6000,
   *   panelClass: ['my-custom-snack'],
   * });
   */
  showSnackbar(config: SnackbarConfig): MatSnackBarRef<TextOnlySnackBar> {
    const typeClassMap: Record<NonNullable<SnackbarConfig['type']>, string> = {
      success: 'snack-success',
      error:   'snack-error',
      warning: 'snack-warning',
      info:    'snack-info',
    };

    const typeClass = config.type ? typeClassMap[config.type] : null;

    const extraClasses = Array.isArray(config.panelClass)
      ? config.panelClass
      : config.panelClass
        ? [config.panelClass]
        : [];

    const panelClasses = typeClass
      ? [typeClass, ...extraClasses]
      : extraClasses;

    return this.snackBar.open(
      config.message,
      config.actionLabel ?? '',
      {
        duration:           config.duration           ?? 3000,
        horizontalPosition: config.horizontalPosition ?? 'center',
        verticalPosition:   config.verticalPosition   ?? 'bottom',
        panelClass:         panelClasses.length ? panelClasses : undefined,
      }
    );
  }

  /**
   * Opens the rich custom snackbar component (white card with icon + action button).
   * This is the component-based replacement for showSuccessMsg / showErrorMsg.
   *
   * @example
   * // Success with action button (like screenshot)
   * this.helper.showCustomSnackbar({
   *   message: r?.message || 'Created 1 client profile',
   *   type: 'success',
   *   actionLabel: 'View Detail',
   * });
   *
   * // Error — no action button
   * this.helper.showCustomSnackbar({ message: 'Something went wrong', type: 'error' });
   *
   * // Warning at the top
   * this.helper.showCustomSnackbar({
   *   message: 'Please review your input',
   *   type: 'warning',
   *   verticalPosition: 'top',
   *   horizontalPosition: 'right',
   * });
   */
  showCustomSnackbar(
    data: TisSnackbarData,
    duration: number = -1,
    horizontalPosition: MatSnackBarHorizontalPosition = 'center',
    verticalPosition: MatSnackBarVerticalPosition = 'bottom'
  ) {
    return this.snackBar.openFromComponent(TisSnackbarComponent, {
      data,
      duration: duration === -1 ? undefined : duration,
      horizontalPosition,
      verticalPosition,
      panelClass: ['tis-snackbar-panel'],
    });
  }


  isChatServiceError(status: number): boolean {
    const errorMessages: { [key: number]: string } = {
      599: 'Chat Service Down',
      401: 'Chat Service Down or User not authenticated. Please logout and login again.',
      500: 'Chat Service Down or User not authenticated. Please logout and login again.',
    };

    if (errorMessages[status]) {
      this.showErrorMsg(errorMessages[status], 'Error', 4000);
      return true;
    }

    return false;
  }

  /**
   * 
   * @param date 
   * @returns if date passed then date as dd/mm/yyyy otherwise system date as dd/mm/yyyy
   */
  getDateDDMMYYYY(date?: Date): string {
    let dateTime: Date;

    if (date) {
      dateTime = new Date(+date);
    }
    else {
      dateTime = new Date();
    }

    const dateStr = String(dateTime.getDate()).padStart(2, '0') + "/" + String(dateTime.getMonth() + 1).padStart(2, '0') + "/" + dateTime.getFullYear();

    return dateStr;
  }

  getDateDDMMYYYYHHMM(date?: Date): string {
    let dateTime: Date;

    if (date) {
      dateTime = new Date(+date);
    }
    else {
      dateTime = new Date();
    }

    const dateStr = String(dateTime.getDate()).padStart(2, '0') + "/" + String(dateTime.getMonth() + 1).padStart(2, '0') + "/" + dateTime.getFullYear() + " at " + String(dateTime.getHours()).padStart(2, '0') + ":" + String(dateTime.getMinutes()).padStart(2, '0') + " " + (dateTime.getHours() >= 12 ? 'PM' : 'AM');

    return dateStr;
  }

  addDaysToDate(date: Date, days: number): Date {
    return new Date(date.getTime() + (days * 24 * 60 * 60 * 1000));
  }

  differenceBetweenDatesInDays(date1: Date, date2: Date): number {
    var diff = Math.abs(date1.getTime() - date2.getTime());
    return Math.ceil(diff / (24 * 60 * 60 * 1000));
  }

  async compressFile(image: any, mimeType: string): Promise<Blob> {

    let imageData = image;

    let imageBlob: Blob = this.dataURItoBlob(imageData.split(',')[1], mimeType);
    return imageBlob;
  }

  getDataUrlFromFile(file: any) {
    return new Promise((resolve, reject) => {
      var reader = new FileReader();
      reader.onload = (e: any) => {
        resolve(e.target.result)
      };
      reader.onerror = (e: any) => {
        reject(e.target.error);
      };
      reader.readAsDataURL(file);
    })
  }

  dataURItoBlob(dataURI: string, mimeType: string): Blob {
    const byteString = window.atob(dataURI);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([int8Array], { type: mimeType });
    return blob;
  }

  /**
   * 
   * @param imgUrl
   * @returns if imgUrl passed then image is returned data URL as base64 string otherwise Hthai Clienteling logo is returned data URL as base64 string
   */
  localImageToBase64(imgUrl = '') {
    const url = imgUrl != '' ? imgUrl : '../../../../assets/logo/hthai-clienteling-logo.png';

    return new Promise((resolve, reject) => {
      var xhr = new XMLHttpRequest();
      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.onload = function () {
        var reader = new FileReader();
        reader.onloadend = function () {
          resolve(reader.result);
        }
        reader.readAsDataURL(xhr.response);
      };
      xhr.send();
    })

  }

  convertBase64ToBlob(Base64Image: string) {
    // split into two parts
    const parts = Base64Image.split(";base64,")
    // hold the content type
    const imageType = parts[0].split(":")[1]
    // decode base64 string
    const decodedData = window.atob(parts[1])
    // create unit8array of size same as row data length
    const uInt8Array = new Uint8Array(decodedData.length)
    // insert all character code into uint8array
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i)
    }
    // return blob image after conversion
    return new Blob([uInt8Array], { type: imageType })
  }

  uploadImageByEntity(data: any): Observable<any> {
    return this.http.post(
      "/image/upload-image-by-entity",
      JSON.stringify(data)
    );
  }

  returnByAttr(arr: any, attr: any, value: any) {
    return arr.find((item: any) => {
      return item[attr] == value;
    });
  }

  generateUniqueId(len = 7) {
    return Math.random()
      .toString(35)
      .substr(2, len);
  }

  mergeArray(data: any, keyForCompare: string, mergeKey: string, mergeType: string) {
    let items: any = [];
    data.map((item: any, index: number) => {
      data.forEach((item2: any, index2: number) => {
        if (index != index2) {
          if (item[keyForCompare] === item2[keyForCompare]) {
            switch (mergeType) {
              case 'number':
                item[mergeKey] = item.noOfDays + item2.noOfDays;
                break;

              case 'array':
                item[mergeKey] = item.noOfDays + item2.noOfDays;
                break;

              default:
                item[mergeKey] = item.noOfDays + item2.noOfDays;
            }

            if (item?.endDate) {
              item.endDate = item2.endDate;
            }
          }
        }
      });

      if (!items.filter((r: any) => r[keyForCompare] == item[keyForCompare])?.length) {
        items.push(item);
      }
    });

    return items?.length > 0 ? items : null;
  }

  uploadImageForPlants(data: any): Observable<any> {
    return this.http.post('/image/upload-image-tagg-by-entity', JSON.stringify(data));
  }

  encode(value: string | number): string {
    if (typeof value === 'number') {
      return this.encodeText(this.encodeNumber(value));
    }
    else if (typeof value === 'string') {
      return this.encodeText(value);
    }

    return value;
  }

  decode(value: string, type = 1): string | number {
    if (type == 1) {
      return this.decodeNumber(this.decodeText(value));
    }
    else if (type == 1) {
      return this.decodeText(value);
    }

    return value;
  }

  encodeText(text: string): string {
    // Convert text to byte array (UTF-8)
    const bytes = Uint8Array.from(text, (c) => c.charCodeAt(0));
    // Encode byte array to Base64
    const encodedText = btoa(String.fromCharCode(...bytes));
    return Base64.encodeURI(encodedText);
  }

  decodeText(encodedText: string): string {
    // Decode Base64 string to byte array
    encodedText = Base64.decode(encodedText);
    const decodedBytes = atob(encodedText);
    // Convert byte array back to text (UTF-8)
    let decodedString = '';
    for (let i = 0; i < decodedBytes.length; i++) {
      decodedString += String.fromCharCode(decodedBytes.charCodeAt(i));
    }
    return decodedString;
  }

  encodeNumber(number: number): string {
    // Convert the number to a string
    const numberString = number.toString();
    // Encode each digit using the mapping
    let encodedText = "";
    for (let i = 0; i < numberString.length; i++) {
      const digit: any = numberString.charAt(i);
      encodedText += mapping[digit] + "--"; // Add space as separator
    }

    // Remove trailing space
    return encodedText.trim();
  }

  decodeNumber(encodedText: string): number {
    // Split the encoded text by spaces
    let encodedWords = encodedText.split("--");
    encodedWords = encodedWords?.filter(e => e != '');

    // Decode each word using the mapping (reverse lookup)
    let decodedNumber = "";
    for (const word of encodedWords) {
      if (word != '') {
        const value = Object.keys(mapping).find((key: any) => mapping[key] == word);
        if (value !== undefined) {
          decodedNumber += value;
        } else {
          // Handle invalid characters (optional)
          console.warn("Warning: Invalid word found in encoded text:", word);
        }
      }
    }

    return parseInt(decodedNumber);
  }

  sortArrayByOrder(A: string[], B: string[]): string[] {
    const sortedA = [];
    for (const element of B) {
      const index = A.indexOf(element);
      if (index !== -1) {
        sortedA.push(A[index]);
        A.splice(index, 1);
      }
    }
    return [...sortedA, ...A]; // Combine sorted elements with remaining elements
  }

  roundNumber(number: number, decimals = 0) {
    const multiplier = Math.pow(10, decimals);
    return Math.round(number * multiplier) / multiplier;
  }

  arrangeDataByKey(data: any[], key: string, appendKey: string[] | null = null): any {
    const resultMap: any[] = [];

    data.forEach(item => {
      const existingEntry = resultMap.find((entry: any) => entry[key] === item[key]);
      if (existingEntry) {
        existingEntry.data.push(item);
      } else {
        let res: any = {};

        if (appendKey && appendKey?.length) {
          appendKey?.forEach(e => {
            res[e] = item[e];
          });
        }
        res[key] = item[key];

        resultMap.push({
          ...res,
          data: [item]
        });
      }
    });

    return resultMap;
  }

  removeDuplicates(arr: any[]) {
    return [...new Set(arr)];
  }

  setTraceQrData(data: any) {
    this.traceQrData = data;
  }

  getTraceQrData() {
    return this.traceQrData;
  }

  prepareQRCodeData(data: any) {
    let title = data?.traceQrDetails?.heading;
    let footerText: any[] = [];
    let basicDetails: any[] = data?.traceQrDetails?.data?.find((d: any) => d.code == 'basicData')?.data ?? [];

    if (data.qrTypeId == 1) {
      let plantedOn = basicDetails?.find((d: any) => d.code == 'plantedDate')?.value ?? null;
      footerText = [
        `Plant Lot No: ${data.lotNo}`,
        `Planted On: ${plantedOn ? DateTime.fromMillis(+plantedOn).toFormat('dd MMM yyyy') : ''}`
      ];
    }
    else if (data.qrTypeId == 2) {
      let harvestedOn = basicDetails?.find((d: any) => d.code == 'harvestedDate')?.value ?? null;
      footerText = [
        `Harvest Lot No: ${data.lotNo}`,
        `Harvested On: ${harvestedOn ? DateTime.fromMillis(+harvestedOn).toFormat('dd MMM yyyy') : ''}`
      ];
    }
    else if (data.qrTypeId == 3) {
      let startDate = basicDetails?.find((d: any) => d.code == 'startDate')?.value ?? null;
      footerText = [
        `Production Batch No: ${data.lotNo}`,
        `Production Start Date: ${startDate ? DateTime.fromMillis(+startDate).toFormat('dd MMM yyyy') : ''}`
      ];
    }
    else if (data.qrTypeId == 4) {
      let packedOn = basicDetails?.find((d: any) => d.code == 'packedOn')?.value ?? null;
      footerText = [
        `Packing Lot No.: ${data.lotNo}`,
        `Packed On: ${packedOn ? DateTime.fromMillis(+packedOn).toFormat('dd MMM yyyy') : ''}`
      ];
    }
    else if (data.qrTypeId == 5) {
      let packedOn = basicDetails?.find((d: any) => d.code == 'packedOn')?.value ?? null;
      footerText = [
        `Packing Serial No.: ${data.lotNo}`,
        `Packed On: ${packedOn ? DateTime.fromMillis(+packedOn).toFormat('dd MMM yyyy') : ''}`
      ];
    }
    let qrData = {
      qrCodeUrl: `${window.origin}/p/tq?id=${data.id}&vk=${this.encode(Number(data.id))}`,
      title: title,
      footerText: footerText,
      qrCodeTitle: data.lotNo,
      filename: `trace-lot-${data.lotNo}-qr-code`
    };

    return qrData;
  }

  generateUUID(): string {
    return 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] % 16) | 0;
      return r.toString(16);
    });
  }

  generateRandomString(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&';
    const charactersLength = characters.length;

    let result = '';
    do {
      result = '';
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
      }
    } while (this.generatedStrings.has(result));

    this.generatedStrings.add(result);
    return result;
  }

  getImageDimensions(file: string) {
    return new Promise(function (resolved, rejected) {
      var i = new Image()
      i.onload = () => {
        resolved({ width: i.width, height: i.height })
      };
      i.onerror = () => {
        rejected({ width: null, height: null, msg: "Can`t fetch" });
      };
      i.src = file
    })
  }

  getBase64FromImageUrl(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous'; // Enable CORS for external images

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas context not available');

          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = (err) => reject(`Image load error: ${err}`);
      img.src = url;
    });
  }

  genCharArray(charA: string, charZ: string) {
    var a = [], i = charA.charCodeAt(0), j = charZ.charCodeAt(0);
    for (; i <= j; ++i) {
      a.push(String.fromCharCode(i));
    }
    return a;
  }

  fixHeader(data: any) {
    let arrayToObject = []
    console.log('Import Data: ', data)
    let header = this.genCharArray('A', 'Z');
    for (let i = 0; i < data.length; i++) {
      let finalObj: any = {};
      for (let j = 0; j < data[i].length; j++) {
        if (!data[i][j]) {
          finalObj[header[j]] = ''
        } else {
          finalObj[header[j]] = data[i][j];
        }
      }
      arrayToObject.push(finalObj)
    }
    return arrayToObject
  }

  currency(amount: number) {
    if (amount >= 1000) {
      let amt = amount.toString();
      let amtArray = amt.split(".");
      //console.log(amtArray);
      let finalAmount = amtArray[0];
      let am = '';
      let indx = (finalAmount.length - 3);
      for (let index = finalAmount.length; index > 0; index--) {
        //console.log("index = ",index);

        if (index == indx) {

          if (index == 3) {
            am = ',' + am;
            indx = -1;
          }
          else {
            //console.log("indx = ",indx);
            am = ',' + am;
            indx = indx - 3;
          }

        }
        //console.log((index - 1), finalAmount[(index - 1)]);

        am = finalAmount[(index - 1)] + am;

      }

      if (amtArray[1]) {
        am = am + '.' + amtArray[1];
      }
      //console.log(amt.length);

      //console.log("a-amount",am);
      return am;
    }
    else {
      return amount;
    }
  }

}
